import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { EszkozService } from '../eszkoz.service';

/*  MEGTÉRÜLÉS – a bérbe adott tárgyi eszközök kimutatása (/eszkoz/megterules).
 *
 *  Eszközönként a bekerülési érték szemben a bérleti díjjal és a nem aktivált kiadásokkal: mennyi
 *  jött már vissza, megtérült-e (és mikor), és ha még nem, a havi átlagos nettó hozamból és a
 *  tervezett (jövőbeli dátumú) sorokból becsült megtérülés. A haszon pénzügyi szemmel (hozam −
 *  bekerülési érték) és számvitelileg (bevétel − kiadás − elszámolt écs) is látszik.
 *  A sorok a könyvelésből (Tárgyi eszközhöz gomb) jönnek, vagy itt kézzel vehetők fel – a kiadás
 *  ráaktiválás nélkül. Számolás: backend/kettos-eszkoz.js (megterules()). */
@Component({
  selector: 'app-eszkoz-megterules',
  templateUrl: './megterules.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', './megterules.component.scss'],
})
export class MegterulesComponent implements OnInit, OnDestroy {
  adat: any = null;
  betolt = false;
  reszlet: any = null;          // a megnyitott eszköz (sorokkal)
  uj: any = null;               // kézi sor
  ujEszkoz: string | null = null;
  private sub?: Subscription;

  constructor(private eszkoz: EszkozService, private toast: ToastController, private alert: AlertController, private router: Router) {}

  get aktiv() { return this.eszkoz.aktiv; }
  private get u() { return this.aktiv.ugyfelId; }

  async ngOnInit() {
    await this.eszkoz.init();
    this.sub = this.eszkoz.aktiv$.subscribe(() => this.frissit());
  }
  ngOnDestroy() { this.sub?.unsubscribe(); }

  async frissit() {
    if (!this.u) { this.adat = null; return; }
    this.betolt = true;
    try { this.adat = await this.eszkoz.getAbs(`/api/eszkoz/megterules/${this.u}`); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni a kimutatást.'); }
    finally { this.betolt = false; }
  }

  async reszletNyit(id: string) {
    try {
      const r: any = await this.eszkoz.getAbs(`/api/eszkoz/megterules/${this.u}?eszkoz_id=${id}`);
      this.reszlet = r.eszkozok?.[0] || null;
      this.uj = null;
      this.szabalyok = this.reszlet ? await this.eszkoz.getAbs(`/api/eszkoz/hozam-szabaly/${this.u}/${id}`) : [];
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült megnyitni.'); }
  }

  /* ── Automatikus hozzárendelés: a partner könyvelt számlái magától az eszközhöz kerülnek ── */
  szabalyok: any[] = [];
  ujSzabaly: any = null;
  szabalyUj(tipus: 'bevetel' | 'kiadas') { this.ujSzabaly = { tipus, partner_nev: '', kulcsszo: '', tol: '' }; }
  async szabalyMent() {
    const sz = this.ujSzabaly;
    if (!sz || (!sz.partner_nev.trim() && !sz.kulcsszo.trim())) { this.hiba('Adj meg partnert vagy kulcsszót.'); return; }
    try {
      const r: any = await this.eszkoz.postAbs(`/api/eszkoz/hozam-szabaly/${this.u}`, { ...sz, eszkoz_id: this.reszlet.id });
      this.ok(r.kotve ? `Szabály mentve – ${r.kotve} korábbi bizonylat hozzárendelve.` : 'Szabály mentve – a következő könyvelt számla magától ide kerül.');
      this.ujSzabaly = null;
      const id = this.reszlet.id;
      await this.frissit();
      await this.reszletNyit(id);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }
  async szabalyTorol(sz: any) {
    const a = await this.alert.create({ header: 'Szabály törlése',
      message: sz.kotve ? `A szabály ${sz.kotve} sort rendelt az eszközhöz. Ezek is törlődjenek?` : 'A szabály törlődik.',
      buttons: [{ text: 'Mégse', role: 'cancel' }, ...(sz.kotve ? [{ text: 'Csak a szabály', role: 'szabaly' }] : []), { text: sz.kotve ? 'Sorokkal együtt' : 'Törlés', role: 'mind' }] });
    await a.present();
    const { role } = await a.onDidDismiss();
    if (role !== 'szabaly' && role !== 'mind') return;
    try {
      await this.eszkoz.delAbs(`/api/eszkoz/hozam-szabaly/${this.u}/${sz.id}${role === 'mind' ? '?sorokkal=1' : ''}`);
      const id = this.reszlet.id;
      await this.frissit();
      await this.reszletNyit(id);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }
  /** Még nem bérbe adott eszköz felvétele a kimutatásba: a részlet nyílik, és jöhet az első sor. */
  async ujEszkozNyit() {
    if (!this.ujEszkoz) return;
    await this.reszletNyit(this.ujEszkoz);
    this.ujEszkoz = null;
    this.ujSor('bevetel');
  }

  ujSor(tipus: 'bevetel' | 'kiadas') {
    this.uj = { tipus, datum: new Date().toISOString().slice(0, 10), megnevezes: '', netto: null, afa: null, brutto: null, megjegyzes: '' };
  }
  get maiNap(): string { return new Date().toISOString().slice(0, 10); }
  async ujMent() {
    if (!this.reszlet || !this.uj) return;
    const netto = Number(this.uj.netto) || 0; const afa = Number(this.uj.afa) || 0;
    const brutto = Number(this.uj.brutto) || netto + afa;
    if (!netto && !brutto) { this.hiba('Add meg az összeget.'); return; }
    try {
      await this.eszkoz.postAbs(`/api/eszkoz/hozam/${this.u}`, { eszkoz_id: this.reszlet.id, tipus: this.uj.tipus, datum: this.uj.datum,
        megnevezes: this.uj.megnevezes, netto: netto || brutto, afa, brutto, megjegyzes: this.uj.megjegyzes });
      this.ok(this.uj.datum > this.maiNap ? 'Tervezett sor felvéve – a becslésbe beleszámít.' : 'Felvéve.');
      const id = this.reszlet.id;
      await this.frissit();
      await this.reszletNyit(id);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }
  async sorTorol(s: any) {
    const a = await this.alert.create({ header: 'Sor törlése', message: s.tetel_id ? 'A kötés megszűnik – a könyvelt bizonylat megmarad.' : 'A kézi sor törlődik.',
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'ok' }] });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'ok') return;
    try {
      await this.eszkoz.delAbs(`/api/eszkoz/hozam/${this.u}/${s.id}`);
      const id = this.reszlet.id;
      await this.frissit();
      await this.reszletNyit(id);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }

  /** A sor összege a kimutatás szerint (áfa-alanynál nettó, alanyi mentesnél bruttó). */
  osszeg(s: any): number { return Number(this.adat?.afa_alany === false ? s.brutto : s.netto) || 0; }
  tervezett(s: any): boolean { return String(s.datum) > this.maiNap; }
  arany(e: any): number { return Math.max(0, Math.min(100, Number(e.megterules_szazalek) || 0)); }
  honapSzoveg(h: number | null): string {
    if (h == null) return '';
    const ev = Math.floor(h / 12); const ho = Math.round(h - ev * 12);
    return [ev ? `${ev} év` : '', ho ? `${ho} hónap` : ''].filter(Boolean).join(' ') || '1 hónapon belül';
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1800, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2800, color: 'danger' })).present(); }
}
